
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    InputAdornment,
    Tooltip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import SearchIcon from "@mui/icons-material/Search";
import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

const API_URL = "https://rukap.edu.in/leave-api";
// const API_URL = "http://127.0.0.1:5000";

export default function EmployeeAccounts() {
    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [entitlement, setEntitlement] = useState("");
    const [taken, setTaken] = useState("");

    const [passwordOpen, setPasswordOpen] = useState(false);
    const [newPassword, setNewPassword] = useState("");

    const [deleteTarget, setDeleteTarget] = useState(null);

    const getConfig = () => ({
        headers: {
            Authorization: `Bearer ${sessionStorage.getItem("leaveToken")}`,
        },
    });

    const loadEmployees = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const user = JSON.parse(
                sessionStorage.getItem("leaveUser") || "{}"
            );

            const role = String(user.role || "").toUpperCase();

            if (role !== "ADMIN" && role !== "REGISTRAR") {
                navigate("/leave-portal", { replace: true });
                return;
            }

            const response = await axios.get(
                `${API_URL}/api/admin/employee-accounts`,
                getConfig()
            );

            setEmployees(response.data.employees || []);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Unable to load employee accounts."
            );
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!sessionStorage.getItem("leaveToken")) {
            navigate("/leave-portal/login", { replace: true });
            return;
        }

        loadEmployees();
    }, [loadEmployees, navigate]);

    const openEdit = (employee) => {
        setSelected(employee);
        setEntitlement(String(employee.annual_leave_entitlement ?? 0));
        setTaken(String(employee.leave_taken ?? 0));
        setEditOpen(true);
        setError("");
        setMessage("");
    };

    const saveEmployee = async () => {
        const annual = Number(entitlement);
        const leaveTaken = Number(taken);

        if (
            !Number.isInteger(annual) ||
            !Number.isInteger(leaveTaken) ||
            annual < 0 ||
            leaveTaken < 0 ||
            leaveTaken > annual
        ) {
            setError(
                "Enter valid non-negative whole numbers. Leave taken cannot exceed entitlement."
            );
            return;
        }

        setSaving(true);
        setError("");

        try {
            await axios.patch(
                `${API_URL}/api/admin/employee-accounts/${selected.employee_id}`,
                {
                    annual_leave_entitlement: annual,
                    leave_taken: leaveTaken,
                },
                getConfig()
            );

            setEditOpen(false);
            setMessage("Employee leave details updated successfully.");
            await loadEmployees();
        } catch (err) {
            setError(
                err.response?.data?.message || "Unable to update employee."
            );
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async () => {
        if (newPassword.length < 8) {
            setError("The new password must contain at least 8 characters.");
            return;
        }

        setSaving(true);
        setError("");

        try {
            await axios.patch(
                `${API_URL}/api/admin/employee-accounts/${selected.employee_id}/password`,
                { newPassword },
                getConfig()
            );

            setPasswordOpen(false);
            setNewPassword("");
            setMessage("Employee password reset successfully.");
        } catch (err) {
            setError(
                err.response?.data?.message || "Unable to reset password."
            );
        } finally {
            setSaving(false);
        }
    };

    const deactivateEmployee = async () => {
        setSaving(true);
        setError("");

        try {
            await axios.delete(
                `${API_URL}/api/admin/employee-accounts/${deleteTarget.employee_id}`,
                getConfig()
            );

            setDeleteTarget(null);
            setMessage(
                "Employee account deactivated. Historical records are preserved."
            );
            await loadEmployees();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Unable to deactivate employee account."
            );
        } finally {
            setSaving(false);
        }
    };

    const filteredEmployees = employees.filter((employee) =>
        [
            employee.employee_code,
            employee.employee_name,
            employee.username,
            employee.designation,
            employee.department_name,
            employee.employee_type,
        ].some((value) =>
            String(value || "").toLowerCase().includes(search.toLowerCase())
        )
    );

    return (
        <>
            <Navbar />

            <Container maxWidth="xl" sx={{ py: 3, minHeight: "65vh" }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            color="#0B3C5D"
                        >
                            Employee Account Management
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage employee accounts and leave balances.
                        </Typography>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/leave-portal/admin-dashboard")}
                    >
                        Back to Dashboard
                    </Button>
                </Stack>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                )}

                {message && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        onClose={() => setMessage("")}
                    >
                        {message}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by employee code, name, username or department"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={{ mb: 2 }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ border: "1px solid #dce3ea", overflowX: "auto" }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#eef3f7" }}>
                                    <TableCell>Employee Code</TableCell>
                                    <TableCell>Employee Name</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Password</TableCell>
                                    <TableCell>Employee Type</TableCell>
                                    <TableCell align="right">Entitlement</TableCell>
                                    <TableCell align="right">Leave Taken</TableCell>
                                    <TableCell align="right">Available</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredEmployees.map((employee) => {
                                    const annual = Number(
                                        employee.annual_leave_entitlement || 0
                                    );
                                    const leaveTaken = Number(employee.leave_taken || 0);

                                    return (
                                        <TableRow hover key={employee.employee_id}>
                                            <TableCell>{employee.employee_code || "-"}</TableCell>
                                            <TableCell>{employee.employee_name}</TableCell>
                                            <TableCell>{employee.username || "-"}</TableCell>
                                            <TableCell>{employee.password || "-"}</TableCell>
                                            <TableCell>
                                                {employee.employee_type?.replace("_", " ") || "-"}
                                            </TableCell>
                                            <TableCell align="right">{annual}</TableCell>
                                            <TableCell align="right">{leaveTaken}</TableCell>
                                            <TableCell align="right">
                                                {Math.max(0, annual - leaveTaken)}
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    justifyContent="center"
                                                >
                                                    <Tooltip title="Edit leave details">
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => openEdit(employee)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Reset password">
                                                        <IconButton
                                                            color="secondary"
                                                            size="small"
                                                            onClick={() => {
                                                                setSelected(employee);
                                                                setNewPassword("");
                                                                setPasswordOpen(true);
                                                                setError("");
                                                            }}
                                                        >
                                                            <LockResetIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Deactivate employee account">
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => setDeleteTarget(employee)}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {filteredEmployees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No employee accounts found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Passwords are never displayed. Use Reset Password to assign a new one.
                    Deactivation preserves employee and leave history.
                </Typography>
            </Container>

            <Footer />

            {/* Edit leave balance */}
            <Dialog
                open={editOpen}
                onClose={() => !saving && setEditOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Edit Employee Leave Details</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {selected?.employee_name} ({selected?.employee_code})
                    </Typography>

                    <TextField
                        fullWidth
                        margin="normal"
                        type="number"
                        label="Annual Leave Entitlement"
                        value={entitlement}
                        onChange={(event) => setEntitlement(event.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        type="number"
                        label="Leave Taken"
                        value={taken}
                        onChange={(event) => setTaken(event.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button disabled={saving} onClick={() => setEditOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={saveEmployee}
                    >
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset password */}
            <Dialog
                open={passwordOpen}
                onClose={() => !saving && setPasswordOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Reset Employee Password</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Account: {selected?.username}
                    </Typography>
                    <TextField
                        fullWidth
                        autoComplete="new-password"
                        type="password"
                        label="New Password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        helperText="Use at least 8 characters."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        disabled={saving}
                        onClick={() => setPasswordOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={resetPassword}
                    >
                        {saving ? "Saving..." : "Reset Password"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm deactivation */}
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => !saving && setDeleteTarget(null)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Deactivate Employee Account?</DialogTitle>
                <DialogContent>
                    This will disable the account for{" "}
                    <strong>{deleteTarget?.employee_name}</strong>. Employee and leave
                    history will be preserved.
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        disabled={saving}
                        onClick={() => setDeleteTarget(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={saving}
                        onClick={deactivateEmployee}
                    >
                        {saving ? "Processing..." : "Deactivate"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
